import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FileInputControl, Form } from '@/core/components';

import { Id } from '@feature/utils';
import { DOCUMENT_UPLOAD_ACCEPT, MAX_UPLOAD_SIZE_MB, useUploadNotification } from '@feature/utils/upload/fileUpload';

import { useMobilityCertificateUpload } from '../../model/MobilityQuery';

interface MobilityCertificateUploadModalProps {
  mobilityId: Id;
  onClose: () => void;
}

const schema = z.object({
  file: z.custom<File>(value => value instanceof File, { params: { i18n: 'custom.fileRequired' } }),
});

type MobilityCertificateUploadSchema = z.infer<typeof schema>;

export function MobilityCertificateUploadModal({ mobilityId, onClose }: MobilityCertificateUploadModalProps) {
  const { t } = useTranslation();
  const notification = useUploadNotification();
  const { mutateAsync, isPending } = useMobilityCertificateUpload(mobilityId);

  const form = useForm<MobilityCertificateUploadSchema>({
    resolver: zodResolver(schema),
  });

  // Promise handlers still run after an unmount, unlike mutate-level callbacks.
  const handleSubmit = (values: MobilityCertificateUploadSchema) =>
    mutateAsync(values.file).then(() => {
      notification.success();
      onClose();
    }, notification.error);

  const handleClose = () => {
    if (!isPending) onClose();
  };

  return (
    <Modal
      opened
      centered
      onClose={handleClose}
      closeButtonProps={{ disabled: isPending }}
      title={t('mobilities.certificate.upload')}
      size="md"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={handleClose}
        isSubmitting={isPending}
      >
        <FileInputControl
          name="file"
          label={t('mobilities.certificate.file')}
          description={t('mobilities.certificate.fileHint', { maxSize: MAX_UPLOAD_SIZE_MB })}
          accept={DOCUMENT_UPLOAD_ACCEPT}
          isSubmitting={isPending}
          withAsterisk
          control={form.control}
        />
      </Form>
    </Modal>
  );
}
